/**
 * **Buscar personas escribiendo**, que es la regla permanente que fijó dirección el 18/10/2026 al
 * probar la #70: *todo filtro o selector de personas que se cree en la ERP tiene que permitir
 * buscar escribiendo, por nombre, apellidos y código*.
 *
 * Vive aquí, en un util propio, para que la regla se cumpla igual en los selectores que vengan
 * detrás —la #59 y la #61 llevan los suyos— sin que cada pantalla vuelva a decidir qué es «coincide
 * con lo que he escrito». Lo que una pantalla decide es de dónde sale su lista de personas; cómo se
 * busca dentro de ella, no.
 *
 * ## Cómo busca
 *
 *  - **Por nombre y por código**, que es lo que hay: en `EmpleadoMO` el nombre completo es un solo
 *    campo (`nombre`, con nombre y apellidos dentro) y el código es el `username`, el que el Mapa de
 *    aprobación enseña debajo del nombre. Así que buscar por apellidos es buscar en `nombre`, y no
 *    hay ningún campo `apellidos` que mirar. Quien tenga otros campos los pasa en `campos`.
 *  - **Sin tildes y sin mayúsculas**: «Pérez» se encuentra escribiendo «perez», y «Muñoz»,
 *    «munoz». Quien busca a alguien no sabe cómo se escribió su ficha.
 *  - **Palabra a palabra y en cualquier orden**: «martin lau» encuentra a «Laura Martín». Teclear
 *    el nombre y el apellido es lo primero que se hace, y con una sola cadena no encontraba nada.
 *  - Cada palabra vale como **trozo**: «ere» encuentra «Pérez» y «mart» encuentra «LMARTIN». Es lo
 *    que hace que el código sirva de atajo sin tener que escribirlo entero.
 *
 * Con el buscador vacío devuelve la lista entera: «no he escrito nada» no es «nadie coincide».
 *
 * @param personas la lista de personas, tal cual la tenga la pantalla
 * @param texto    lo que se ha escrito en el buscador
 * @param campos   opcional: en qué campos buscar. Por defecto, el nombre y el código
 * @return las personas que coinciden, en el mismo orden en que venían
 */
function buscarPersonas(personas, texto, campos) {
    const lista = personas || [];
    const palabras = palabrasDe(texto);
    if (!palabras.length) {
        return lista.slice();
    }
    const donde = campos && campos.length ? campos : CAMPOS_POR_DEFECTO;
    return lista.filter(function(persona) {
        const aguja = sinTildesNiCaja(donde.map(function(campo) {
            const valor = persona ? persona[campo] : null;
            return valor === null || valor === undefined ? '' : String(valor);
        }).join(' '));
        return palabras.every(function(palabra) { return aguja.indexOf(palabra) !== -1; });
    });
}

/** El nombre completo y el código, que es lo que lleva cualquier lista de personas del ERP. */
const CAMPOS_POR_DEFECTO = ['nombre', 'username'];

/** Lo escrito, en palabras y ya normalizadas. Los espacios de más no son una palabra. */
function palabrasDe(texto) {
    return sinTildesNiCaja(texto === null || texto === undefined ? '' : texto)
        .split(/\s+/)
        .filter(function(palabra) { return !!palabra; });
}

/**
 * El texto comparable: sin diacríticos y en minúsculas.
 *
 * Los diacríticos se quitan **por código** —los combinantes van de U+0300 a U+036F, que es a lo que
 * `normalize('NFD')` separa la tilde— y no con una clase de caracteres en una expresión regular: un
 * escape `\uXXXX` dentro de un literal de expresión regular se ha llegado a escribir como el
 * carácter de verdad y ahí parte el fichero entero.
 */
function sinTildesNiCaja(texto) {
    const cadena = String(texto);
    const descompuesto = cadena.normalize ? cadena.normalize('NFD') : cadena;
    let limpio = '';
    for (let i = 0; i < descompuesto.length; i++) {
        const codigo = descompuesto.charCodeAt(i);
        if (codigo >= 0x300 && codigo <= 0x36f) continue;
        limpio += descompuesto.charAt(i);
    }
    return limpio.toLowerCase();
}

export default buscarPersonas;
